/**
 * Home — Algomed HomeHubPage'in Telegram WebApp adaptasyonu.
 *
 * Algomed mirror'ı:
 * - Hero greeting ("Merhaba {ad}") + tier badge + brand glyph (HomeHubPage hero)
 * - Resume widget (HomeHubPage > resumeTarget Flex bloğu) — yalnızca localStorage'da kayıt varsa
 * - 6 modül grid (HomeHubPage > MODULES → SimpleGrid LinkBox'lar)
 *   • Soru Çöz / Flash Kart / Dijital Antrenör / Dijital Mentor / Konu Evreni / AlgoSPOT Notları
 * - 3 hızlı stat (HomeHubPage > HeroStatChip → StatCard'a sadeleştirildi)
 * - Abone footer kart (SubscriptionStatusBanner muadili kompakt CTA)
 *
 * Veri: api.me() + api.usage() · streak/today/srs alanları
 *   backend'de gelmiyor → usage ve linked durumu üzerinden türetiliyor.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Heading,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { tg, getTelegramUser, haptic } from '../telegram';
import { BRAND, ICON, type Tier } from '@algobot/shared';
import HubCard from '../components/HubCard';
import StatCard from '../components/StatCard';
import ResumeCard from '../components/ResumeCard';
import PlanBadge from '../components/PlanBadge';

// Canonical Me tipi — lib/api.ts'den al (algomedUserId/subscriptionEndsAt dahil).
import type { MeResp as Me } from '../lib/api';

interface UsageRow {
  feature: string;
  used: number;
  limit: number | null;
  resetAt: string;
}

type ResumeKind = 'question' | 'flashcard' | 'algospot' | 'lecture';

interface ResumeEntry {
  kind: ResumeKind;
  to: string;
  label: string;
  context?: string | null;
  glyph: string;
  timestamp: number;
}

const RESUME_STORAGE_KEYS: Array<{
  key: string;
  kind: ResumeKind;
  label: string;
  glyph: string;
  buildTo: (parsed: any) => string | null;
}> = [
  {
    key: 'algobot_resume_question',
    kind: 'question',
    label: 'Son sorudan devam',
    glyph: BRAND.emojiContent.question,
    buildTo: (p) => (p?.id ? `/question/${p.id}` : '/question'),
  },
  {
    key: 'algobot_resume_flashcard',
    kind: 'flashcard',
    label: 'Flashcard tekrarına devam',
    glyph: BRAND.emojiContent.flashcard,
    buildTo: (p) => (p?.id ? `/flashcard/${p.id}` : '/flashcard'),
  },
  {
    key: 'algobot_resume_algospot',
    kind: 'algospot',
    label: 'AlgoSpot okumaya devam',
    glyph: BRAND.emojiContent.algospot,
    buildTo: (p) => (p?.id ? `/algospot/${p.id}` : '/algospot'),
  },
  {
    key: 'algobot_resume_lecture',
    kind: 'lecture',
    label: 'Konu anlatımına devam',
    glyph: BRAND.emojiContent.lecture,
    buildTo: (p) => (p?.id ? `/lecture/${p.id}` : null),
  },
];

function readLatestResume(): ResumeEntry | null {
  if (typeof window === 'undefined') return null;
  let best: ResumeEntry | null = null;
  for (const candidate of RESUME_STORAGE_KEYS) {
    try {
      const raw = window.localStorage.getItem(candidate.key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const ts = Number(parsed?.timestamp) || 0;
      if (!ts) continue;
      const to = candidate.buildTo(parsed);
      if (!to) continue;
      if (!best || ts > best.timestamp) {
        best = {
          kind: candidate.kind,
          to,
          label: candidate.label,
          context: parsed?.topicName || parsed?.title || null,
          glyph: candidate.glyph,
          timestamp: ts,
        };
      }
    } catch {
      // localStorage erişim hatası veya bozuk JSON — yoksay
    }
  }
  return best;
}

function dismissResumeKind(kind: ResumeKind) {
  if (typeof window === 'undefined') return;
  const entry = RESUME_STORAGE_KEYS.find((c) => c.kind === kind);
  if (!entry) return;
  try {
    window.localStorage.removeItem(entry.key);
  } catch {
    // no-op
  }
}

interface UsageMap {
  question?: UsageRow;
  flashcard?: UsageRow;
  algospot?: UsageRow;
  srs?: UsageRow;
}

function buildUsageMap(rows: UsageRow[]): UsageMap {
  const map: UsageMap = {};
  for (const row of rows) {
    if (row.feature === 'question_bank_daily') map.question = row;
    else if (row.feature === 'flashcard_daily') map.flashcard = row;
    else if (row.feature === 'algospot_daily') map.algospot = row;
    else if (row.feature === 'srs_review_daily') map.srs = row;
  }
  return map;
}

export default function HomePage() {
  const nav = useNavigate();
  const tgUser = getTelegramUser();
  const platform = tg()?.platform || 'web';
  const [me, setMe] = useState<Me | null>(null);
  const [usage, setUsage] = useState<UsageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ status?: number; message: string } | null>(null);
  const [resume, setResume] = useState<ResumeEntry | null>(null);

  // iOS Telegram'da üst safe-area zaten AppShell'de uygulanıyor; sayfa içi
  // dock-altı boşluk Telegram bottom button alanına çarpmasın diye:
  const bottomPad = platform === 'ios' ? 8 : platform === 'android' ? 6 : 4;

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, u] = await Promise.all([
        api.me() as Promise<Me>,
        api.usage().catch(() => [] as UsageRow[]),
      ]);
      setMe(m);
      setUsage(u || []);
    } catch (err: any) {
      const status = err?.status as number | undefined;
      if (status === 401) {
        setError({ status, message: 'Bu mini uygulama Telegram içinde açılmalı.' });
      } else {
        setError({ status, message: 'Algomed bilgilerine ulaşılamadı.' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    setResume(readLatestResume());
  }, []);

  const heroGradient = useColorModeValue(
    'linear(to-br, brand.500, blue.500)',
    'linear(to-br, brand.700, blue.700)',
  );
  const heroOverlay = useColorModeValue('blackAlpha.100', 'blackAlpha.200');
  const heroChipBg = useColorModeValue('whiteAlpha.300', 'whiteAlpha.200');

  const usageMap = useMemo(() => buildUsageMap(usage), [usage]);

  const tier = (me?.tier || 'free').toLowerCase() as Tier;
  const displayName =
    me?.displayName || tgUser?.first_name || tgUser?.username || 'Algomedli';

  const greeting = displayName ? `Merhaba ${displayName}` : 'Merhaba';

  const questionUsage = usageMap.question;
  const questionRemaining = questionUsage?.limit
    ? Math.max(0, questionUsage.limit - questionUsage.used)
    : null;
  const flashcardUsage = usageMap.flashcard;
  const flashcardRemaining = flashcardUsage?.limit
    ? Math.max(0, flashcardUsage.limit - flashcardUsage.used)
    : null;
  const srsUsage = usageMap.srs;
  const srsRemaining = srsUsage?.limit ? Math.max(0, srsUsage.limit - srsUsage.used) : null;

  // Loading state
  if (loading) {
    return (
      <Container maxW="md" py={10}>
        <VStack spacing={3}>
          <Spinner size="xl" color="accent" thickness="3px" />
          <Text color="textSecondary" fontSize="sm">
            Algomed yükleniyor…
          </Text>
        </VStack>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxW="md" py={6}>
        <Alert
          status="warning"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          textAlign="center"
          borderRadius="xl"
          py={6}
          bg="bgCard"
          borderWidth="1px"
          borderColor="borderPrimary"
        >
          <AlertIcon boxSize="32px" color="warningIcon" />
          <AlertTitle mt={3} fontSize="md" color="textPrimary">
            {error.status === 401 ? 'Telegram içinde aç' : 'Bağlanılamadı'}
          </AlertTitle>
          <AlertDescription fontSize="sm" color="textSecondary" mt={2}>
            {error.message}
          </AlertDescription>
          <Button mt={4} colorScheme="brand" size="sm" onClick={load}>
            Tekrar dene
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="md" py={3} px={3} pb={bottomPad}>
      <VStack spacing={3} align="stretch">
        {/* HERO · greeting + tier */}
        <Box
          position="relative"
          overflow="hidden"
          borderWidth="1px"
          borderColor="borderPrimary"
          borderRadius="2xl"
          boxShadow="cardElevated"
          bgGradient={heroGradient}
          color="white"
          px={4}
          py={3.5}
        >
          <Box position="absolute" inset={0} bg={heroOverlay} pointerEvents="none" />
          <Box
            position="absolute"
            top="-22%"
            right="-10%"
            w="120px"
            h="120px"
            borderRadius="full"
            bg="whiteAlpha.200"
            filter="blur(18px)"
            opacity={0.6}
            pointerEvents="none"
          />
          <VStack position="relative" zIndex={1} align="stretch" spacing={2}>
            <HStack justify="space-between" align="center" spacing={2}>
              <HStack spacing={2.5} minW={0}>
                <Flex
                  w="42px"
                  h="42px"
                  borderRadius="lg"
                  bg={heroChipBg}
                  borderWidth="1px"
                  borderColor="whiteAlpha.400"
                  align="center"
                  justify="center"
                  flexShrink={0}
                >
                  <Text fontSize="xl" lineHeight="1" fontWeight="bold">
                    {BRAND.emojiBrand}
                  </Text>
                </Flex>
                <VStack align="start" spacing={0} minW={0}>
                  <Text fontWeight="bold" fontSize="xl" lineHeight="1.1" noOfLines={1}>
                    {greeting}
                  </Text>
                  <Text fontSize="xs" opacity={0.9} noOfLines={1}>
                    Algomed Mini App · {platform}
                  </Text>
                </VStack>
              </HStack>
              <PlanBadge tier={tier} />
            </HStack>

            {(!me?.linked || tier === 'free') && (
              <Text fontSize="xs" opacity={0.95} pt={1}>
                {me?.linked
                  ? 'Ücretsiz planda günlük kotaların açık. Daha fazlası için planı yükselt.'
                  : 'Daha kişisel deneyim için Telegram hesabını Algomed web hesabına bağla.'}
              </Text>
            )}
          </VStack>
        </Box>

        {/* RESUME · Kaldığın yerden devam */}
        {resume ? (
          <ResumeCard
            eyebrow="KALDIĞIN YERDEN DEVAM"
            title={resume.label}
            context={resume.context}
            glyph={resume.glyph}
            ctaLabel="Devam et"
            onResume={() => nav(resume.to)}
            onDismiss={() => {
              dismissResumeKind(resume.kind);
              setResume(null);
            }}
          />
        ) : null}

        {/* MODULE GRID · 6 kart */}
        <SimpleGrid columns={2} spacing={2.5}>
          <HubCard
            title="Soru Çöz"
            subtitle={questionRemaining !== null ? `${questionRemaining} soru kaldı` : 'Klasik test pratiği'}
            glyph={BRAND.emojiContent.question}
            iconColor="accent"
            lightTileBg="blue.50"
            onClick={() => nav('/question')}
          />
          <HubCard
            title="Flash Kart"
            subtitle={flashcardRemaining !== null ? `${flashcardRemaining} kart kaldı` : 'Aktif hatırlama tekrarı'}
            glyph={BRAND.emojiContent.flashcard}
            iconColor="flashcardAccent"
            lightTileBg="purple.50"
            onClick={() => nav('/flashcard')}
          />
          <HubCard
            title="Dijital Antrenör"
            subtitle="SRS · zayıf konu rotası"
            glyph={ICON.trainer}
            iconColor="successIcon"
            lightTileBg="green.50"
            onClick={() => nav('/trainer')}
          />
          <HubCard
            title="Dijital Mentor"
            subtitle="Sınav stratejisi ve plan"
            glyph={ICON.mentor}
            iconColor="coachAccent"
            lightTileBg="purple.50"
            onClick={() => nav('/mentor')}
          />
          <HubCard
            title="Konu Evreni"
            subtitle="Sınav · branş · konu seç"
            glyph={BRAND.emojiContent.lecture}
            iconColor="textPrimary"
            lightTileBg="gray.100"
            onClick={() => nav('/topics')}
          />
          <HubCard
            title="AlgoSPOT Notları"
            subtitle="Anahtar bilgiler"
            glyph={BRAND.emojiContent.algospot}
            iconColor="warningIcon"
            lightTileBg="yellow.50"
            onClick={() => nav('/algospot')}
          />
        </SimpleGrid>

        {/* QUICK STATS · 3 kutu */}
        <SimpleGrid columns={3} spacing={2}>
          <StatCard
            label="Soru"
            value={questionUsage ? `${questionUsage.used}/${questionUsage.limit ?? '∞'}` : '0'}
            glyph={BRAND.emojiContent.question}
            accent="accent"
            helper="bugün"
          />
          <StatCard
            label="Flash"
            value={flashcardUsage ? `${flashcardUsage.used}/${flashcardUsage.limit ?? '∞'}` : '0'}
            glyph={BRAND.emojiContent.flashcard}
            accent="flashcardAccent"
            helper="bugün"
          />
          <StatCard
            label="SRS"
            value={srsUsage ? `${srsUsage.used}/${srsUsage.limit ?? '∞'}` : '0'}
            glyph={BRAND.emojiContent.srs}
            accent="successIcon"
            helper={srsRemaining !== null ? `${srsRemaining} bekliyor` : 'tekrar'}
          />
        </SimpleGrid>

        {/* SUBSCRIPTION FOOTER · abone CTA */}
        <Box
          bg="bgCard"
          borderWidth="1px"
          borderColor="borderPrimary"
          borderRadius="xl"
          boxShadow="cardElevated"
          px={3.5}
          py={3}
        >
          <HStack justify="space-between" align="center" spacing={3}>
            <HStack spacing={2.5} minW={0}>
              <Flex
                w="36px"
                h="36px"
                borderRadius="lg"
                bg="bgSecondary"
                borderWidth="1px"
                borderColor="borderSecondary"
                align="center"
                justify="center"
                flexShrink={0}
              >
                <Text fontSize="lg" lineHeight="1" color="accent" fontWeight="bold">
                  {ICON.payment}
                </Text>
              </Flex>
              <VStack align="start" spacing={0} minW={0}>
                <Text fontSize="sm" fontWeight="bold" color="textPrimary" noOfLines={1}>
                  {tier === 'free' ? 'Planı Yükselt' : 'Aboneliğini Yönet'}
                </Text>
                <Text fontSize="11px" color="textSecondary" noOfLines={1}>
                  {tier === 'free' ? 'Sınırsız soru ve konu anlatımı' : `Aktif: ${tier.toUpperCase()}`}
                </Text>
              </VStack>
            </HStack>
            <Button
              size="sm"
              colorScheme="brand"
              onClick={() => {
                haptic('select');
                nav('/subscription');
              }}
            >
              {tier === 'free' ? BRAND.copy.upgrade : 'Yönet'}
            </Button>
          </HStack>
        </Box>

        {/* CONNECT FOOTER · varsa */}
        {!me?.linked && !me?.shadow ? (
          <Box
            bg="bgSecondary"
            borderWidth="1px"
            borderStyle="dashed"
            borderColor="borderPrimary"
            borderRadius="xl"
            px={3.5}
            py={3}
          >
            <HStack justify="space-between" align="center" spacing={3}>
              <VStack align="start" spacing={0} minW={0}>
                <Text fontSize="sm" fontWeight="semibold" color="textPrimary" noOfLines={1}>
                  {BRAND.copy.linkCta}
                </Text>
                <Text fontSize="11px" color="textSecondary" noOfLines={2}>
                  Web hesabınla eşleştir, ilerlemen birleşsin.
                </Text>
              </VStack>
              <Button
                size="sm"
                variant="outline"
                colorScheme="brand"
                onClick={() => {
                  haptic('select');
                  nav('/connect');
                }}
              >
                Bağla
              </Button>
            </HStack>
          </Box>
        ) : null}

        <Box textAlign="center" pt={1}>
          <Text fontSize="10px" color="textMuted">
            Algomed · {platform} · v0.2
          </Text>
        </Box>
      </VStack>
    </Container>
  );
}
